/**
 * SAB-1017 / SAB-1020 — the league's waiver rules, client side.
 *
 * The AUTHORITY is SQL: `nfl_waiver_settings(uuid)` supplies the defaults and
 * `nfl_set_waiver_settings(uuid, jsonb)` enforces the commissioner check and the FAAB lock.
 * Nothing here is a control. This module exists so the settings card can render the right
 * affordance BEFORE the user submits — a disabled input with a reason beats an input that
 * looks live and then throws.
 */

export interface WaiverSettings {
  enabled: boolean;
  drop_lock_days: number;
  faab_budget: number;
  /**
   * SAB-1023: days after the draft during which EVERY unrostered player is on waivers.
   *
   * Default 0 here on purpose. New leagues carry a stored 1 written at creation; a reader
   * default of 1 would switch the rule on for every league that has already drafted, and
   * would make this card show a number that exists nowhere.
   */
  post_draft_waiver_days: number;
}

/** Platform defaults — the same values `nfl_waiver_settings()` returns for an absent key. */
export const WAIVER_DEFAULTS: WaiverSettings = {
  enabled: true,
  drop_lock_days: 2,
  faab_budget: 100,
  post_draft_waiver_days: 0,
};

/**
 * Parse whatever is in `settings.waivers` into a usable shape.
 *
 * Deliberately tolerant in the same way `leaguePositionLimits` is: this arrives from a jsonb
 * column and nothing upstream validates it. A malformed value degrades to the platform
 * default rather than throwing on a render path.
 *
 * `drop_lock_days: 0` must survive — it is a real value meaning "straight to free agency",
 * and the `Number(val) || default` shortcut would silently turn it back into 2.
 */
export function parseWaiverSettings(raw: unknown): WaiverSettings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...WAIVER_DEFAULTS };
  const o = raw as Record<string, unknown>;
  const int = (v: unknown, dflt: number) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isInteger(n) && n >= 0 ? n : dflt;
  };
  return {
    enabled: typeof o.enabled === 'boolean' ? o.enabled : WAIVER_DEFAULTS.enabled,
    drop_lock_days: int(o.drop_lock_days, WAIVER_DEFAULTS.drop_lock_days),
    faab_budget: int(o.faab_budget, WAIVER_DEFAULTS.faab_budget),
    post_draft_waiver_days: int(o.post_draft_waiver_days, WAIVER_DEFAULTS.post_draft_waiver_days),
  };
}

/**
 * League statuses that count as "before week 1".
 *
 * MIRRORS the list inside `nfl_set_waiver_settings`, and that is a real (small) duplication
 * worth naming: the SQL refuses regardless of what this says, so the worst this can do is
 * show a live input that the server then rejects with FAAB_LOCKED — never the reverse.
 *
 * It is NOT keyed on `current_week`, which is the trap: `nfl_leagues.current_week` DEFAULTS
 * TO 1, so a brand-new pre-draft league that never set the column reads 1 and would look
 * locked before it ever drafted.
 */
const PRE_SEASON_STATUSES = ['pre_draft', 'drafting', 'draft_completed'];

/** A NULL/unknown status reads as locked — for a money setting the safe default is refuse. */
export function isFaabLocked(leagueStatus: string | null | undefined): boolean {
  return !PRE_SEASON_STATUSES.includes(leagueStatus ?? '');
}

/**
 * The waiver cron's UTC time, as [hour, minute].
 *
 * THIS IS THE SAME FACT AS THE CRON SCHEDULE and must move with it:
 * supabase/migrations/20260902164512_sab1017_waiver_cron_0600.sql sets `0 6 * * *`.
 * Named as a constant rather than buried in the formatter so the next person changing the
 * schedule can grep `0 6 * * *` or this array and find both halves.
 */
// SAB-1142c: the NIGHTLY cron hour. Kept because nextNightlyClear is anchored to it; the
// formatter that used to render it as a single "waivers process at X" string is deleted,
// because there is no single answer any more — the nightly and weekly runs are on different
// anchors and the weekly one does not shift. Surfaces state both classes explicitly.
export const WAIVER_CRON_UTC: readonly [number, number] = [6, 0];

